import { MS_PER_DAY, MS_PER_MINUTE } from "@/lib/constants";
import {
  PetYardWeatherCondition,
  PetYardWeatherResponse,
  PlayyardSky,
  PlayyardSkyPhase,
  PlayyardSkyPreview,
} from "@/types";

type Rgb = [number, number, number];

interface SkyPalette {
  skyTop: Rgb;
  skyMid: Rgb;
  skyBottom: Rgb;
  sun: Rgb;
  farHill: Rgb;
  midHill: Rgb;
  nearHill: Rgb;
  groundTop: Rgb;
  groundBottom: Rgb;
  tuft: Rgb;
  waterTop: Rgb;
  waterBottom: Rgb;
  cloud: Rgb;
  tint: Rgb;
  tintAlpha: number;
  glow: number;
}

const COLOR_KEYS = [
  "skyTop",
  "skyMid",
  "skyBottom",
  "sun",
  "farHill",
  "midHill",
  "nearHill",
  "groundTop",
  "groundBottom",
  "tuft",
  "waterTop",
  "waterBottom",
  "cloud",
  "tint",
] as const;

const hex = (value: string): Rgb => [
  Number.parseInt(value.slice(1, 3), 16),
  Number.parseInt(value.slice(3, 5), 16),
  Number.parseInt(value.slice(5, 7), 16),
];

const PALETTES: Record<"DAWN" | "DAY" | "GOLDEN" | "NIGHT", SkyPalette> = {
  DAWN: {
    skyTop: hex("#8d86c9"),
    skyMid: hex("#f7b3a6"),
    skyBottom: hex("#ffe6bf"),
    sun: hex("#ffe0a3"),
    farHill: hex("#b1a6cf"),
    midHill: hex("#95b684"),
    nearHill: hex("#6ea55a"),
    groundTop: hex("#88c464"),
    groundBottom: hex("#579a42"),
    tuft: hex("#4a8a37"),
    waterTop: hex("#fbc8b4"),
    waterBottom: hex("#9c96d0"),
    cloud: hex("#fff0ea"),
    tint: hex("#ffbeaa"),
    tintAlpha: 0.1,
    glow: 0.35,
  },
  DAY: {
    skyTop: hex("#3d8ee6"),
    skyMid: hex("#8cc8ff"),
    skyBottom: hex("#d9f0ff"),
    sun: hex("#fff6c9"),
    farHill: hex("#9fbfd8"),
    midHill: hex("#76bb61"),
    nearHill: hex("#56a946"),
    groundTop: hex("#7ccc59"),
    groundBottom: hex("#46983a"),
    tuft: hex("#3b8a31"),
    waterTop: hex("#9fd6ff"),
    waterBottom: hex("#3f86c9"),
    cloud: hex("#ffffff"),
    tint: hex("#000000"),
    tintAlpha: 0,
    glow: 0.2,
  },
  GOLDEN: {
    skyTop: hex("#35285f"),
    skyMid: hex("#e9805d"),
    skyBottom: hex("#ffd37c"),
    sun: hex("#ffd06e"),
    farHill: hex("#7c5d8f"),
    midHill: hex("#7d9150"),
    nearHill: hex("#5d8a3d"),
    groundTop: hex("#8aaa4a"),
    groundBottom: hex("#4b772f"),
    tuft: hex("#3f6a28"),
    waterTop: hex("#f3a26f"),
    waterBottom: hex("#6a5a8f"),
    cloud: hex("#ffd7b8"),
    tint: hex("#ff823c"),
    tintAlpha: 0.14,
    glow: 0.55,
  },
  NIGHT: {
    skyTop: hex("#070b24"),
    skyMid: hex("#17204e"),
    skyBottom: hex("#2d3b73"),
    sun: hex("#f5f0cf"),
    farHill: hex("#1d2450"),
    midHill: hex("#1c3834"),
    nearHill: hex("#183126"),
    groundTop: hex("#23452c"),
    groundBottom: hex("#12281a"),
    tuft: hex("#0f2416"),
    waterTop: hex("#2b3b78"),
    waterBottom: hex("#0e1636"),
    cloud: hex("#3a4478"),
    tint: hex("#0a1446"),
    tintAlpha: 0.42,
    glow: 0.25,
  },
};

// How much each condition greys out the sky and how busy the clouds get.
const CLOUD_COVER: Record<PetYardWeatherCondition, number> = {
  [PetYardWeatherCondition.CLEAR]: 0,
  [PetYardWeatherCondition.PARTLY_CLOUDY]: 0.2,
  [PetYardWeatherCondition.OVERCAST]: 0.6,
  [PetYardWeatherCondition.FOG]: 0.5,
  [PetYardWeatherCondition.DRIZZLE]: 0.6,
  [PetYardWeatherCondition.RAIN]: 0.75,
  [PetYardWeatherCondition.SNOW]: 0.6,
  [PetYardWeatherCondition.THUNDERSTORM]: 0.9,
};
const CLOUD_COUNT: Record<PetYardWeatherCondition, number> = {
  [PetYardWeatherCondition.CLEAR]: 2,
  [PetYardWeatherCondition.PARTLY_CLOUDY]: 4,
  [PetYardWeatherCondition.OVERCAST]: 7,
  [PetYardWeatherCondition.FOG]: 3,
  [PetYardWeatherCondition.DRIZZLE]: 6,
  [PetYardWeatherCondition.RAIN]: 7,
  [PetYardWeatherCondition.SNOW]: 6,
  [PetYardWeatherCondition.THUNDERSTORM]: 7,
};
const WET_CONDITIONS = new Set([
  PetYardWeatherCondition.DRIZZLE,
  PetYardWeatherCondition.RAIN,
  PetYardWeatherCondition.SNOW,
  PetYardWeatherCondition.THUNDERSTORM,
]);
const RAINY_CONDITIONS = new Set([
  PetYardWeatherCondition.DRIZZLE,
  PetYardWeatherCondition.RAIN,
  PetYardWeatherCondition.THUNDERSTORM,
]);
const SNOW_WHITE = hex("#e9eef5");
const WINDY_MPH = 18;
const FREEZING_F = 32;
const FIREFLY_MIN_F = 60;
// Used when the weather API is down: a plain 6:45am–6:45pm day.
const FALLBACK_SUNRISE_MINUTES = 6 * 60 + 45;
const FALLBACK_SUNSET_MINUTES = 18 * 60 + 45;

const lerp = (from: number, to: number, amount: number) =>
  from + (to - from) * amount;

const mixRgb = (from: Rgb, to: Rgb, amount: number): Rgb => [
  lerp(from[0], to[0], amount),
  lerp(from[1], to[1], amount),
  lerp(from[2], to[2], amount),
];

const greyed = (color: Rgb, amount: number): Rgb => {
  const luminance =
    (color[0] * 0.299 + color[1] * 0.587 + color[2] * 0.114) * 0.9;
  return mixRgb(color, [luminance, luminance, luminance], amount);
};

const mixPalette = (
  from: SkyPalette,
  to: SkyPalette,
  amount: number,
): SkyPalette => {
  const mixed = { ...from };
  COLOR_KEYS.forEach((key) => {
    mixed[key] = mixRgb(from[key], to[key], amount);
  });
  mixed.tintAlpha = lerp(from.tintAlpha, to.tintAlpha, amount);
  mixed.glow = lerp(from.glow, to.glow, amount);
  return mixed;
};

const toCss = ([red, green, blue]: Rgb) =>
  `rgb(${Math.round(red).toString()} ${Math.round(green).toString()} ${Math.round(blue).toString()})`;

// Anchor sunrise/sunset to the day nearest `now`, so an hour-old cache from
// yesterday still lines up after midnight.
const getSunTimes = (now: number, weather: PetYardWeatherResponse) => {
  let sunrise: number;
  let sunset: number;
  if (weather) {
    sunrise = new Date(weather.sunrise).getTime();
    sunset = new Date(weather.sunset).getTime();
  } else {
    const midnight = new Date(now).setHours(0, 0, 0, 0);
    sunrise = midnight + FALLBACK_SUNRISE_MINUTES * MS_PER_MINUTE;
    sunset = midnight + FALLBACK_SUNSET_MINUTES * MS_PER_MINUTE;
  }
  const dayShift =
    Math.round((now - (sunrise + sunset) / 2) / MS_PER_DAY) * MS_PER_DAY;
  return { sunrise: sunrise + dayShift, sunset: sunset + dayShift };
};

// Palette keyframes across the day; between stops the palettes blend.
const getPaletteStops = (sunrise: number, sunset: number) =>
  [
    [sunrise - 60 * MS_PER_MINUTE, PALETTES.NIGHT],
    [sunrise - 15 * MS_PER_MINUTE, PALETTES.DAWN],
    [sunrise + 45 * MS_PER_MINUTE, PALETTES.DAWN],
    [sunrise + 120 * MS_PER_MINUTE, PALETTES.DAY],
    [sunset - 120 * MS_PER_MINUTE, PALETTES.DAY],
    [sunset - 60 * MS_PER_MINUTE, PALETTES.GOLDEN],
    [sunset, PALETTES.GOLDEN],
    [sunset + 45 * MS_PER_MINUTE, PALETTES.NIGHT],
  ] as const;

const getTimePalette = (now: number, sunrise: number, sunset: number) => {
  const stops = getPaletteStops(sunrise, sunset);
  const nextIndex = stops.findIndex(([time]) => now < time);
  if (nextIndex <= 0) return PALETTES.NIGHT;
  const [fromTime, fromPalette] = stops[nextIndex - 1];
  const [toTime, toPalette] = stops[nextIndex];
  return mixPalette(
    fromPalette,
    toPalette,
    (now - fromTime) / (toTime - fromTime),
  );
};

const getEveningProgress = (
  phase: PlayyardSkyPhase,
  time: number,
  sunset: number,
) => {
  if (phase === PlayyardSkyPhase.BLUE_HOUR || phase === PlayyardSkyPhase.NIGHT)
    return 1;
  if (phase !== PlayyardSkyPhase.GOLDEN_HOUR) return 0;
  return (time - (sunset - 60 * MS_PER_MINUTE)) / (60 * MS_PER_MINUTE);
};

const getPhase = (
  now: number,
  sunrise: number,
  sunset: number,
): PlayyardSkyPhase => {
  if (now < sunrise - 15 * MS_PER_MINUTE) return PlayyardSkyPhase.NIGHT;
  if (now < sunrise + 60 * MS_PER_MINUTE) return PlayyardSkyPhase.DAWN;
  if (now < sunset - 60 * MS_PER_MINUTE) return PlayyardSkyPhase.DAY;
  if (now < sunset) return PlayyardSkyPhase.GOLDEN_HOUR;
  if (now < sunset + 45 * MS_PER_MINUTE) return PlayyardSkyPhase.BLUE_HOUR;
  return PlayyardSkyPhase.NIGHT;
};

// A representative moment for each previewed phase.
const getPreviewTime = (
  phase: PlayyardSkyPhase,
  sunrise: number,
  sunset: number,
) => {
  if (phase === PlayyardSkyPhase.DAWN) return sunrise + 15 * MS_PER_MINUTE;
  if (phase === PlayyardSkyPhase.DAY) return (sunrise + sunset) / 2;
  if (phase === PlayyardSkyPhase.GOLDEN_HOUR)
    return sunset - 30 * MS_PER_MINUTE;
  if (phase === PlayyardSkyPhase.BLUE_HOUR) return sunset + 22 * MS_PER_MINUTE;
  return sunset + 4 * 60 * MS_PER_MINUTE;
};

// Position along an arc across the sky, as % of the scene. It starts and
// ends at the horizon, behind the hills.
const getArcPosition = (progress: number) => ({
  x: 8 + progress * 84,
  y: 52 - Math.sin(progress * Math.PI) * 42,
});

const getSunAndMoon = (now: number, sunrise: number, sunset: number) => {
  const dayProgress = (now - sunrise) / (sunset - sunrise);
  const nightLength = MS_PER_DAY - (sunset - sunrise);
  const sinceSunset = now > sunset ? now - sunset : now - (sunset - MS_PER_DAY);
  const nightProgress = sinceSunset / nightLength;
  const isSunUp = dayProgress > -0.04 && dayProgress < 1.04;
  return {
    sun: {
      ...getArcPosition(Math.min(Math.max(dayProgress, 0), 1)),
      opacity: isSunUp ? 1 : 0,
    },
    moon: {
      ...getArcPosition(Math.min(Math.max(nightProgress, 0), 1)),
      opacity: isSunUp ? 0 : 1,
    },
  };
};

const applyWeather = (
  palette: SkyPalette,
  condition: PetYardWeatherCondition,
  isFreezing: boolean,
): SkyPalette => {
  const cover = CLOUD_COVER[condition] * 0.65;
  const result = { ...palette };
  COLOR_KEYS.forEach((key) => {
    if (key === "tint") return;
    result[key] = greyed(palette[key], cover);
  });
  result.glow = palette.glow * (1 - CLOUD_COVER[condition]);

  // Snow settles on the ground; a hard frost only dusts it.
  const whiteness =
    condition === PetYardWeatherCondition.SNOW ? 1 : isFreezing ? 0.3 : 0;
  if (whiteness > 0) {
    result.groundTop = mixRgb(result.groundTop, SNOW_WHITE, 0.75 * whiteness);
    result.groundBottom = mixRgb(
      result.groundBottom,
      SNOW_WHITE,
      0.65 * whiteness,
    );
    result.nearHill = mixRgb(result.nearHill, SNOW_WHITE, 0.6 * whiteness);
    result.midHill = mixRgb(result.midHill, SNOW_WHITE, 0.45 * whiteness);
    result.farHill = mixRgb(result.farHill, SNOW_WHITE, 0.3 * whiteness);
    result.tuft = mixRgb(result.tuft, SNOW_WHITE, 0.4 * whiteness);
  }
  return result;
};

const toVars = (palette: SkyPalette): Record<string, string> => {
  const vars: Record<string, string> = {};
  COLOR_KEYS.forEach((key) => {
    if (key === "tint") return;
    vars[`--yard-${key}`] = toCss(palette[key]);
  });
  const [red, green, blue] = palette.tint.map(Math.round);
  vars["--yard-tint"] =
    `rgb(${red.toString()} ${green.toString()} ${blue.toString()} / ${palette.tintAlpha.toFixed(3)})`;
  vars["--yard-glow"] = palette.glow.toFixed(3);
  return vars;
};

export const playyardSky = {
  // Deterministic 0–1 noise so scattered decor (tufts, stars) stays put
  // across renders without storing positions.
  random: (seed: number): number => {
    const value = Math.sin(seed * 12.9898) * 43758.5453;
    return value - Math.floor(value);
  },

  /**
   * The full scene state for a moment: blended palette, sun/moon arc, and
   * weather. `preview` overrides the live phase/condition for admins.
   */
  compute: ({
    now,
    weather,
    preview,
  }: {
    now: number;
    weather: PetYardWeatherResponse | undefined;
    preview: PlayyardSkyPreview;
  }): PlayyardSky => {
    const liveWeather = weather ?? null;
    const { sunrise, sunset } = getSunTimes(now, liveWeather);
    const time = preview.phase
      ? getPreviewTime(preview.phase, sunrise, sunset)
      : now;
    const condition =
      preview.condition ??
      liveWeather?.condition ??
      PetYardWeatherCondition.CLEAR;
    const temperatureF = liveWeather?.temperatureF ?? FIREFLY_MIN_F + 10;
    const isFreezing = preview.isFreezing || temperatureF <= FREEZING_F;
    const isWindy =
      preview.isWindy || (liveWeather?.windSpeedMph ?? 0) >= WINDY_MPH;

    const timePalette = getTimePalette(time, sunrise, sunset);
    const palette = applyWeather(timePalette, condition, isFreezing);
    const nightAmount = timePalette.tintAlpha / PALETTES.NIGHT.tintAlpha;
    const phase = getPhase(time, sunrise, sunset);
    const cover = CLOUD_COVER[condition];
    const { sun, moon } = getSunAndMoon(time, sunrise, sunset);
    const isEvening =
      phase === PlayyardSkyPhase.BLUE_HOUR ||
      phase === PlayyardSkyPhase.NIGHT ||
      phase === PlayyardSkyPhase.GOLDEN_HOUR;

    return {
      phase,
      condition,
      vars: toVars(palette),
      sun: { ...sun, opacity: sun.opacity * (1 - cover * 0.85) },
      moon: { ...moon, opacity: moon.opacity * (1 - cover * 0.85) },
      starOpacity: Math.max(0, nightAmount * 1.4 - 0.4) * (1 - cover),
      nightAmount: Math.min(1, nightAmount),
      eveningProgress: getEveningProgress(phase, time, sunset),
      cloudCount: CLOUD_COUNT[condition],
      isWindy,
      isFreezing,
      isWet: RAINY_CONDITIONS.has(condition),
      hasFireflies:
        isEvening &&
        !WET_CONDITIONS.has(condition) &&
        !isFreezing &&
        temperatureF >= FIREFLY_MIN_F,
    };
  },
};
